import Phaser from 'phaser';
import { BaseEnemy, ALL_DIRS, Direction, GridQuery, reverseOf } from './BaseEnemy';

/**
 * Lazy seeker. Every 2-3 seconds it picks the best direction toward the
 * closest player and commits for a short burst, then drifts random until
 * the next decision. Speed 70% — slow enough to outrun.
 */
export class Tracker extends BaseEnemy {
  private nextDecisionAt = 0;
  private lastDecisionCell = '';
  private seekUntil = 0;
  private getPlayerPositions: () => Array<{ x: number; y: number; alive: boolean }>;

  constructor(
    scene: Phaser.Scene,
    col: number, row: number,
    grid: GridQuery,
    getPlayerPositions: () => Array<{ x: number; y: number; alive: boolean }>,
  ) {
    super(scene, 'enemy-tracker', col, row, 0.7, grid);
    this.getPlayerPositions = getPlayerPositions;
    this.applyDir(Phaser.Utils.Array.GetRandom(ALL_DIRS) as Direction);
  }

  update(time: number, _delta: number) {
    if (!this.alive) return;
    this.animateWalk(_delta);
    if (!this.isCenteredOnTile(6)) return;

    const { col, row } = this.centerOfTile();
    const cellKey = `${col},${row}`;

    // Switch mode every ~2-3 seconds
    if (time >= this.nextDecisionAt) {
      this.nextDecisionAt = time + Phaser.Math.Between(2000, 3000);
      this.seekUntil = time + 1500; // commit to seek for 1.5s
    }

    // Same cell, already decided this frame's choice — keep going
    if (cellKey === this.lastDecisionCell && this.canMoveTo(this.currentDir)) return;
    this.lastDecisionCell = cellKey;

    const seeking = time < this.seekUntil;
    if (seeking) {
      const dir = this.chooseSeekDir();
      if (dir) { this.applyDir(dir); return; }
    }

    // Fallback: wander predictably (straight until blocked)
    if (this.canMoveTo(this.currentDir)) {
      this.applyDir(this.currentDir);
      return;
    }
    const options = ALL_DIRS.filter(d => this.canMoveTo(d));
    if (options.length === 0) {
      (this.body as Phaser.Physics.Arcade.Body).setVelocity(0, 0);
      return;
    }
    const rev = reverseOf(this.currentDir);
    const nonReverse = options.filter(d => d !== rev);
    const choice = nonReverse.length > 0 ? nonReverse : options;
    this.applyDir(Phaser.Utils.Array.GetRandom(choice) as Direction);
  }

  private chooseSeekDir(): Direction | null {
    const players = this.getPlayerPositions().filter(p => p.alive);
    if (players.length === 0) return null;
    const me = { x: this.x, y: this.y };
    const target = players.reduce((best, p) =>
      Phaser.Math.Distance.BetweenPoints(me, p) < Phaser.Math.Distance.BetweenPoints(me, best) ? p : best,
      players[0]);
    const dx = target.x - this.x;
    const dy = target.y - this.y;
    const prefer: Direction[] = Math.abs(dx) > Math.abs(dy)
      ? [dx > 0 ? 'right' : 'left', dy > 0 ? 'down' : 'up']
      : [dy > 0 ? 'down' : 'up', dx > 0 ? 'right' : 'left'];
    for (const d of prefer) if (this.canMoveTo(d)) return d;
    // All preferred dirs blocked — fall back to any open dir (keep seeking rather than wandering)
    const fallback = ALL_DIRS.filter(d => this.canMoveTo(d) && d !== reverseOf(prefer[0]));
    return fallback.length > 0 ? (Phaser.Utils.Array.GetRandom(fallback) as Direction) : null;
  }
}
